/**
 * @description
 * Util to check data is null, empty etc
 *
 * @publicApi
 *
 */
/* eslint-disable  @typescript-eslint/no-explicit-any */
export class ComparisonUtil {
  static isNil(value: unknown): boolean {
    return value === undefined || value === null;
  }

  static isString(value: unknown): boolean {
    return typeof value === 'string';
  }

  static isObjectEmpty(value: unknown): boolean {
    if (!value) {
      return true;
    }

    return Object.keys(value)?.length === 0 && value.constructor === Object;
  }

  static isObjectNullable(value: unknown): boolean {
    if (this.isObjectEmpty(value)) {
      return true;
    }

    const filtered = Object.keys(value).filter(o => !this.isNil(value[o]));
    return filtered?.length === 0;
  }

  static isArrayNil(value: unknown[]): boolean {
    if (!Array.isArray(value)) {
      return true;
    }

    return value === undefined || value === null || value.length === 0;
  }

  static isEmpty(value: unknown): boolean {
    if (this.isNil(value)) {
      return true;
    }

    if (this.isString(value) && (value as string).replace(/\s/g, '') === '') {
      return true;
    }

    return false;
  }

  static hasWhiteSpace(s: string): boolean {
    const whitespaceChars = [' ', '\t', '\n'];
    return whitespaceChars.some(char => s.includes(char));
  }

  static isNumeric(s: string): boolean {
    if (typeof s !== 'string') return false; // Only process strings
    const num = Number(s);
    return !isNaN(num) && s.trim() !== ''; // Ensure it's not empty or just spaces
  }

  static deepEqual(a: unknown, b: unknown) {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  static deepClean<T>(obj: T): T {
    if (obj === null || obj === undefined) return obj;

    if (typeof obj === 'string') {
      return obj;
    }

    if (Array.isArray(obj)) {
      const arr = obj
        .map(v => this.deepClean(v))
        .filter(
          v =>
            v !== null &&
            v !== undefined &&
            !(typeof v === 'string' && v.trim() === '') &&
            !(Array.isArray(v) && v.length === 0) &&
            !(typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0),
        );

      return arr as any;
    }

    if (typeof obj === 'object') {
      const cleanedEntries = Object.entries(obj)
        .map(([key, value]) => [key, this.deepClean(value)] as [string, any])
        .filter(
          ([_, value]) =>
            value !== null &&
            value !== undefined &&
            !(typeof value === 'string' && value.trim() === '') &&
            !(Array.isArray(value) && value.length === 0) &&
            !(typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0),
        );

      return Object.fromEntries(cleanedEntries) as T;
    }

    return obj;
  }

  static deepFullEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;

    if (typeof a !== 'object' || typeof b !== 'object' || !a || !b) {
      return false;
    }

    const isArrayA = Array.isArray(a);
    const isArrayB = Array.isArray(b);
    if (isArrayA !== isArrayB) return false;

    if (isArrayA && isArrayB) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (!this.deepFullEqual(a[i], b[i])) return false;
      }
      return true;
    }

    const keysA = Object.keys(a).sort();
    const keysB = Object.keys(b).sort();
    if (keysA.length !== keysB.length) return false;

    for (let i = 0; i < keysA.length; i++) {
      const key = keysA[i];
      if (key !== keysB[i]) return false;
      if (!this.deepFullEqual(a[key], b[key])) return false;
    }

    return true;
  }

  static isValidDate(date: Date | string): boolean {
    const parsedDate = new Date(date);
    return !isNaN(parsedDate.getTime());
  }
}
